import { Fragment, createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { dictionaries, type EnumKind, type MessageKey, type PluralKey } from './dictionary';

export type Locale = 'en' | 'ka';
export type { EnumKind, MessageKey, PluralKey };

type Vars = Record<string, string | number>;

export interface Translator {
  locale: Locale;
  t: (key: MessageKey, vars?: Vars) => string;
  /** Picks `<key>.one` or `<key>.other` for `count` and passes `{count}` to the template. */
  tn: (key: PluralKey, count: number, vars?: Vars) => string;
  /** Label for an enum value: `enum.<kind>.<value>`, then the generic `enum.status.<value>`, then the value made readable. */
  enumLabel: (kind: EnumKind, value: string | null | undefined) => string;
  /** Like `t`, but a `{name}` placeholder can be filled with an element, so markup keeps its place in either word order. */
  rich: (key: MessageKey, parts: Record<string, ReactNode>) => ReactNode;
}

interface I18nState extends Translator {
  setLocale: (locale: Locale) => void;
}

function interpolate(template: string, vars?: Vars): string {
  return vars ? template.replace(/\{(\w+)\}/g, (_, k: string) => String(vars[k] ?? '')) : template;
}

function readable(value: string): string {
  return value.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
}

const has = (key: string): key is MessageKey => key in dictionaries.en;

export function createTranslator(locale: Locale): Translator {
  const messages: Record<MessageKey, string> = dictionaries[locale];
  const rules = new Intl.PluralRules(locale === 'ka' ? 'ka-GE' : 'en-GB');
  const t = (key: MessageKey, vars?: Vars) => interpolate(messages[key], vars);
  const tn = (key: PluralKey, count: number, vars?: Vars) => {
    const exact = `${key}.${rules.select(count)}`;
    return t(has(exact) ? exact : (`${key}.other` as MessageKey), { count, ...vars });
  };
  const enumLabel = (kind: EnumKind, value: string | null | undefined) => {
    if (!value) return '—';
    const specific = `enum.${kind}.${value}`;
    if (has(specific)) return t(specific);
    const generic = `enum.status.${value}`;
    return has(generic) ? t(generic) : readable(value);
  };
  const rich = (key: MessageKey, parts: Record<string, ReactNode>) =>
    messages[key].split(/\{(\w+)\}/).map((piece, i) => <Fragment key={i}>{i % 2 === 1 ? parts[piece] : piece}</Fragment>);
  return { locale, t, tn, enumLabel, rich };
}

const translators: Record<Locale, Translator> = { en: createTranslator('en'), ka: createTranslator('ka') };

/** A fixed language regardless of the reader's choice, for previews that show one language on purpose. */
export function translatorFor(locale: Locale): Translator {
  return translators[locale];
}

const I18nContext = createContext<I18nState | null>(null);

export function I18nProvider({ children, initial }: { children: ReactNode; initial?: Locale }) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    try {
      const stored = localStorage.getItem('bulk.locale');
      if (stored === 'en' || stored === 'ka') return stored;
    } catch {
      // ignore
    }
    return initial ?? 'en';
  });
  const setLocale = useCallback((next: Locale) => {
    try {
      localStorage.setItem('bulk.locale', next);
    } catch {
      // ignore
    }
    setLocaleState(next);
  }, []);
  const value = useMemo(() => ({ ...translators[locale], setLocale }), [locale, setLocale]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/** A language of its own, starting from the reader's, that a page drawn inside another can switch without changing the one remembered for the reader. */
export function ScopedI18nProvider({ children }: { children: ReactNode }) {
  const outer = useLocale();
  const [locale, setLocale] = useState<Locale>(outer);
  const value = useMemo(() => ({ ...translators[locale], setLocale }), [locale]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nState {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used inside I18nProvider');
  return ctx;
}

/** The locale alone, for shared controls that may render outside a provider. */
export function useLocale(): Locale {
  return useContext(I18nContext)?.locale ?? 'en';
}

/** Translation for shared controls, falling back to English without a provider. */
export function useTranslator(): Translator {
  return useContext(I18nContext) ?? translators.en;
}

export function useT(): Translator['t'] {
  return useTranslator().t;
}
